import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Save, Cloud, Loader2, CheckCircle2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Admin → Настройки tab. Generic key/value settings editor.  Currently hosts
 * the "free hint via countdown" timer; new globals will be added here.
 */
export default function AdminGeneralSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timerMin, setTimerMin] = useState(1);
  const [cfToken, setCfToken] = useState('');
  const [cfTokenSet, setCfTokenSet] = useState(false);
  const [proxyUrl, setProxyUrl] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [vkToken, setVkToken] = useState('');
  const [vkTokenSet, setVkTokenSet] = useState(false);
  const [vkGroup, setVkGroup] = useState('');
  const [vkTesting, setVkTesting] = useState(false);
  const [vkTestResult, setVkTestResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/admin/settings`, { headers: getAuthHeader() })
      .then(r => {
        setTimerMin(Number(r.data?.free_hint_timer_minutes) || 1);
        setCfTokenSet(Boolean(r.data?.cloudflare_api_token_set));
        setProxyUrl(r.data?.telegram_proxy_url || null);
        setVkTokenSet(Boolean(r.data?.vk_access_token_set));
        setVkGroup(r.data?.vk_group_url || '');
      })
      .catch(() => toast.error('Не удалось загрузить настройки'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { free_hint_timer_minutes: Math.max(0, Math.floor(Number(timerMin) || 0)) };
      if (cfToken.trim()) payload.cloudflare_api_token = cfToken.trim();
      payload.vk_group_url = vkGroup;
      if (vkToken.trim()) payload.vk_access_token = vkToken.trim();
      await axios.put(`${API}/api/admin/settings`, payload, { headers: getAuthHeader() });
      if (cfToken.trim()) {
        setCfTokenSet(true);
        setCfToken('');
      }
      if (vkToken.trim()) {
        setVkTokenSet(true);
        setVkToken('');
      }
      toast.success('Настройки сохранены');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const deployProxy = async () => {
    setDeploying(true);
    setDeployResult(null);
    try {
      const r = await axios.post(`${API}/api/admin/telegram-proxy/deploy`, {}, {
        headers: getAuthHeader(),
        timeout: 120000,
      });
      setProxyUrl(r.data?.proxy_url || null);
      setDeployResult(r.data);
      if (r.data?.test_ok) {
        toast.success('Telegram-прокси развёрнут и работает');
      } else {
        toast.warning(r.data?.message || 'Прокси развёрнут, но проверка не прошла');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Ошибка развёртывания прокси';
      setDeployResult({ test_ok: false, message: msg });
      toast.error(msg);
    } finally {
      setDeploying(false);
    }
  };

  const testVk = async () => {
    setVkTesting(true);
    setVkTestResult(null);
    try {
      const r = await axios.post(`${API}/api/admin/vk/test`, {}, {
        headers: getAuthHeader(),
        timeout: 60000,
      });
      setVkTestResult({ ok: true, message: r.data?.message || 'Связь с VK работает' });
      toast.success('Связь с VK работает');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Ошибка проверки VK';
      setVkTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setVkTesting(false);
    }
  };

  if (loading) return <div style={{ padding: '0.75rem', color: '#666' }}>Загрузка…</div>;

  const cardStyle = {
    background: 'white', padding: '16px 18px', borderRadius: 12,
    border: '1px solid #e5e7eb', boxShadow: '0 2px 6px rgba(15,23,42,0.03)',
  };

  return (
    <div data-testid="admin-general-settings" style={{ maxWidth: 720 }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, fontWeight: 700, marginBottom: 14 }}>
        <SettingsIcon size={20} /> Настройки приложения
      </h2>

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label
            htmlFor="free-hint-timer-min"
            style={{ flex: '1 1 320px', fontSize: 14, color: '#374151', fontWeight: 600 }}
          >
            Таймер для получения бесплатной подсказки (минут)
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, fontWeight: 400 }}>
              По истечении этого времени игрок получит подсказку бесплатно.
              Таймер ставится на паузу, если игрок переключился на другую вкладку.
              0 = отключить эту опцию.
            </div>
          </label>
          <input
            id="free-hint-timer-min"
            data-testid="setting-free-hint-timer-minutes"
            type="number"
            min={0}
            max={120}
            value={timerMin}
            onChange={e => setTimerMin(e.target.value)}
            style={{
              width: 110, padding: '8px 10px', borderRadius: 8,
              border: '1px solid #d1d5db', fontSize: 14, textAlign: 'right',
            }}
          />
        </div>

        <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid #f1f5f9' }}>
          <label
            htmlFor="cloudflare-api-token"
            style={{ display: 'block', fontSize: 14, color: '#374151', fontWeight: 600 }}
          >
            CLOUDFLARE_API_TOKEN
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, fontWeight: 400 }}>
              Токен Cloudflare (шаблон «Edit Cloudflare Workers») для развёртывания
              прокси, через который анонсы отправляются в Telegram в обход блокировки.
              {cfTokenSet && <span style={{ color: '#059669', fontWeight: 600 }}> Токен сохранён.</span>}
            </div>
          </label>
          <input
            id="cloudflare-api-token"
            data-testid="setting-cloudflare-api-token"
            type="password"
            autoComplete="new-password"
            placeholder={cfTokenSet ? '•••••••••••••••• (сохранён — введите новый, чтобы заменить)' : 'Вставьте API-токен Cloudflare'}
            value={cfToken}
            onChange={e => setCfToken(e.target.value)}
            style={{
              marginTop: 8, width: '100%', maxWidth: 480, padding: '8px 10px',
              borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14,
            }}
          />

          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              onClick={deployProxy}
              disabled={deploying || (!cfTokenSet && !cfToken.trim())}
              data-testid="deploy-telegram-proxy-btn"
              title={!cfTokenSet && !cfToken.trim() ? 'Сначала сохраните токен' : 'Развернуть Cloudflare Worker для Telegram'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 18px', borderRadius: 10, border: 'none',
                background: deploying ? '#9ca3af' : '#f38020', color: 'white',
                fontWeight: 600, cursor: deploying ? 'wait' : 'pointer',
                opacity: (!cfTokenSet && !cfToken.trim()) ? 0.5 : 1,
              }}
            >
              {deploying ? <Loader2 size={14} className="animate-spin" /> : <Cloud size={14} />}
              {deploying ? 'Разворачиваем…' : 'Развернуть Telegram-прокси'}
            </button>

            {proxyUrl && (
              <span data-testid="telegram-proxy-status" style={{ fontSize: 12, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <CheckCircle2 size={14} color="#059669" /> Прокси: {proxyUrl}
              </span>
            )}
          </div>

          {deployResult && (
            <div style={{
              marginTop: 10, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
              color: deployResult.test_ok ? '#059669' : '#dc2626',
            }}>
              {deployResult.test_ok ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
              {deployResult.message}
            </div>
          )}
        </div>

        <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid #f1f5f9' }}>
          <label
            htmlFor="vk-access-token"
            style={{ display: 'block', fontSize: 14, color: '#374151', fontWeight: 600 }}
          >
            Ключ доступа Вконтакте
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, fontWeight: 400 }}>
              Пользовательский ключ доступа с правами wall, photos, groups для
              публикации анонсов на стене группы.
              {vkTokenSet && <span style={{ color: '#059669', fontWeight: 600 }}> Ключ сохранён.</span>}
            </div>
          </label>
          <input
            id="vk-access-token"
            data-testid="setting-vk-access-token"
            type="password"
            autoComplete="new-password"
            placeholder={vkTokenSet ? '•••••••••••••••• (сохранён — введите новый, чтобы заменить)' : 'Вставьте ключ доступа VK'}
            value={vkToken}
            onChange={e => setVkToken(e.target.value)}
            style={{
              marginTop: 8, width: '100%', maxWidth: 480, padding: '8px 10px',
              borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14,
            }}
          />

          <label
            htmlFor="vk-group-url"
            style={{ display: 'block', marginTop: 12, fontSize: 14, color: '#374151', fontWeight: 600 }}
          >
            Группа Вконтакте
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, fontWeight: 400 }}>
              Адрес группы для анонсов: https://vk.com/имя, @имя или просто имя группы.
            </div>
          </label>
          <input
            id="vk-group-url"
            data-testid="setting-vk-group-url"
            type="text"
            placeholder="https://vk.com/countryscanner"
            value={vkGroup}
            onChange={e => setVkGroup(e.target.value)}
            style={{
              marginTop: 8, width: '100%', maxWidth: 480, padding: '8px 10px',
              borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14,
            }}
          />

          <div style={{ marginTop: 12 }}>
            <button
              onClick={testVk}
              disabled={vkTesting || (!vkTokenSet && !vkToken.trim())}
              data-testid="vk-test-btn"
              title="Проверить ключ и найти группу (сначала сохраните настройки)"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 18px', borderRadius: 10, border: 'none',
                background: vkTesting ? '#9ca3af' : '#0077FF', color: 'white',
                fontWeight: 600, cursor: vkTesting ? 'wait' : 'pointer',
                opacity: (!vkTokenSet && !vkToken.trim()) ? 0.5 : 1,
              }}
            >
              {vkTesting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {vkTesting ? 'Проверяем…' : 'Проверить связь с VK'}
            </button>
          </div>

          {vkTestResult && (
            <div data-testid="vk-test-result" style={{
              marginTop: 10, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
              color: vkTestResult.ok ? '#059669' : '#dc2626',
            }}>
              {vkTestResult.ok ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
              {vkTestResult.message}
            </div>
          )}
        </div>

        <button
          onClick={save}
          disabled={saving}
          data-testid="settings-save-btn"
          style={{
            marginTop: 18, display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '10px 22px', borderRadius: 10, border: 'none',
            background: saving ? '#9ca3af' : '#0a4797', color: 'white',
            fontWeight: 600, cursor: saving ? 'wait' : 'pointer',
          }}
        >
          <Save size={14} /> {saving ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}
